import React, { useState, useEffect, useRef } from 'react';
import { buyerGroupsAPI } from '../services/api';
import './GroupSelector.css';

export const NO_GROUP_VALUE = 'none';

const GroupSelector = ({ selectedGroups = [], onChange, placeholder = 'Select groups...', multiple = true, showNoGroup = false }) => {
  const [groups, setGroups] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    fetchGroups();
  }, []);

  useEffect(() => {
    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchGroups = async () => {
    try {
      setLoading(true);
      const response = await buyerGroupsAPI.getAll();
      if (response.data && response.data.success === false) {
        setError(response.data.error || 'Failed to load groups');
        setGroups([]);
        return;
      }
      const groupData = response.data?.groups || response.data || [];
      setGroups(Array.isArray(groupData) ? groupData : []);
    } catch (err) {
      setError('Failed to load groups');
      console.error('Error fetching groups:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleGroup = (groupId) => {
    if (groupId === NO_GROUP_VALUE) {
      // No Group is exclusive — toggle it and clear other selections
      onChange(isNoGroupSelected ? [] : [NO_GROUP_VALUE]);
      if (!multiple) setIsOpen(false);
      return;
    }
    if (multiple) {
      // Deselect "No Group" if selecting a real group
      const withoutNoGroup = selectedGroups.filter(id => id !== NO_GROUP_VALUE);
      const newSelection = withoutNoGroup.includes(groupId)
        ? withoutNoGroup.filter(id => id !== groupId)
        : [...withoutNoGroup, groupId];
      onChange(newSelection);
    } else {
      onChange(selectedGroups.includes(groupId) ? [] : [groupId]);
      setIsOpen(false);
    }
  };

  const handleSelectAll = () => {
    if (selectedGroups.length === groups.length) {
      onChange([]);
    } else {
      onChange(groups.map(g => g.id));
    }
  };

  const handleClearSelection = () => {
    onChange([]);
  };

  const isNoGroupSelected = selectedGroups.includes(NO_GROUP_VALUE);

  const getSelectedGroupNames = () => {
    if (selectedGroups.length === 0) return placeholder;
    if (isNoGroupSelected) return 'No Group';
    if (selectedGroups.length === 1) {
      const group = groups.find(g => g.id === selectedGroups[0]);
      return group ? group.name : '';
    }
    return `${selectedGroups.length} groups selected`;
  };

  const handleToggleDropdown = () => {
    if (!isOpen) {
      // Refresh groups when opening dropdown
      fetchGroups();
    }
    setIsOpen(!isOpen);
  };

  return (
    <div className="group-selector" ref={dropdownRef}>
      <div className="group-selector-trigger" onClick={handleToggleDropdown}>
        <span className="selected-text">{getSelectedGroupNames()}</span>
        <span className={`dropdown-arrow ${isOpen ? 'open' : ''}`}>v</span>
      </div>

      {isOpen && (
        <div className="group-selector-dropdown">
          {loading && <div className="dropdown-loading">Loading groups...</div>}

          {error && <div className="dropdown-error">{error}</div>}

          {!loading && !error && groups.length === 0 && (
            <div className="dropdown-empty">
              <p>No groups available</p>
              <small>Create a group first from the Groups page</small>
            </div>
          )}

          {!loading && !error && (
            <>
              {showNoGroup && (
                <div
                  className={`group-option ${isNoGroupSelected ? 'selected' : ''}`}
                  onClick={() => handleToggleGroup(NO_GROUP_VALUE)}
                  style={{ borderBottom: '1px solid var(--border-color)' }}
                >
                  <div className="group-checkbox">
                    {isNoGroupSelected && <span className="checkmark">&#10003;</span>}
                  </div>
                  <div className="group-info">
                    <div className="group-name-row">
                      <span
                        className="group-color-dot"
                        style={{ backgroundColor: 'var(--text-muted)' }}
                      />
                      <span className="group-name" style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>No Group</span>
                    </div>
                    <span className="group-count" style={{ fontSize: '11px' }}>Buyers with no group assigned</span>
                  </div>
                </div>
              )}
            </>
          )}

          {!loading && !error && groups.length > 0 && (
            <>
              {multiple && (
                <div className="dropdown-actions">
                  <button
                    type="button"
                    className="action-btn"
                    onClick={handleSelectAll}
                  >
                    {selectedGroups.length === groups.length ? 'Deselect All' : 'Select All'}
                  </button>
                  {selectedGroups.length > 0 && (
                    <button
                      type="button"
                      className="action-btn"
                      onClick={handleClearSelection}
                    >
                      Clear
                    </button>
                  )}
                </div>
              )}

              <div className="group-options">
                {groups.map(group => (
                  <div
                    key={group.id}
                    className={`group-option ${selectedGroups.includes(group.id) ? 'selected' : ''}`}
                    onClick={() => handleToggleGroup(group.id)}
                  >
                    <div className="group-checkbox">
                      {selectedGroups.includes(group.id) && <span className="checkmark">&#10003;</span>}
                    </div>
                    <div className="group-info">
                      <div className="group-name-row">
                        <span
                          className="group-color-dot"
                          style={{ backgroundColor: group.color || 'var(--tag)' }}
                        />
                        <span className="group-name">{group.name}</span>
                      </div>
                      {group.description && (
                        <span className="group-description">{group.description}</span>
                      )}
                      <span className="group-count">
                        {group.buyer_count || 0} buyer{group.buyer_count !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default GroupSelector;





